import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Button, Row, Col, Form, Label, Input, Progress } from 'reactstrap';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DefaultModal from 'components/Modal/DefaultModal';
import FlexSelect from 'components/Inputs/FlexSelect';
import CustomFieldsInputsForm from 'views/CRM/shared/components/CustomFieldsInputsForm';
import { AlertBox } from 'views/CRM/PriceCatalog/externalForm/components/ChangeDetailsModal/styled';

import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import { useDefaultFieldsObject } from './hooks/useDefaultFieldsObject';
import { useReasons } from './hooks/useReasons';
import { useSubreasons } from './hooks/useSubreasons';
import { useStatuses } from './hooks/useStatuses';
import { useCustomFields } from './hooks/useCustomFields';
import { useCustomFieldsValues } from './hooks/useCustomFieldsValues';
import { useSelectedRecordType } from './hooks/useSelectedRecordType';

import { entitiesName } from 'views/CRM/shared/utils/entitiesName';

import ProtocolService from '../../../service';

import RecordTypeSelection from 'views/RecordType/components/RecordTypeSelection';
import { ConditionalFieldRenderer } from './components/ConditionalFieldRenderer';
import { AutoMappingModal } from './components/AutoMappingModal';

const STEPS = {
  SELECT_RECORD_TYPE: 'SELECT_RECORD_TYPE',
  MAP_DEFAULT_FIELDS: 'MAP_DEFAULT_FIELDS',
  MAP_CUSTOM_FIELDS: 'MAP_CUSTOM_FIELDS',
  CHANGE_SLA: 'CHANGE_SLA',
  OPTIONAL_DESCRIPTION: 'OPTIONAL_DESCRIPTION',
  SUBMIT: 'SUBMIT'
};

const STEP_CONFIG = {
  [STEPS.SELECT_RECORD_TYPE]: {
    title: 'Selecionar Tipo de Registro',
    subtitle: 'Escolha o novo tipo de registro',
    order: 1
  },
  [STEPS.MAP_DEFAULT_FIELDS]: {
    title: 'Mapear Campos Padrão',
    subtitle: 'Configure os campos padrão obrigatórios',
    order: 2
  },
  [STEPS.MAP_CUSTOM_FIELDS]: {
    title: 'Mapear Campos Customizados',
    subtitle: 'Configure os campos personalizados',
    order: 3
  },
  [STEPS.CHANGE_SLA]: {
    title: 'Alterar SLA',
    subtitle: '',
    order: 4
  },
  [STEPS.OPTIONAL_DESCRIPTION]: {
    title: 'Adicionar Descrição (Opcional)',
    subtitle: 'Forneça uma descrição para a mudança',
    order: 5
  },
  [STEPS.SUBMIT]: {
    title: '',
    subtitle: '',
    order: 6
  }
};

const StepIndicator = ({ currentStep }) => {
  const currentOrder = STEP_CONFIG[currentStep]?.order || 1;
  const totalSteps = Object.keys(STEP_CONFIG).length - 1;
  const progressPercentage = (currentOrder / totalSteps) * 100;

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          Etapa {currentOrder} de {totalSteps}
        </small>
        <small className="text-muted">
          {Math.round(progressPercentage)}% concluído
        </small>
      </div>
      <Progress value={progressPercentage} color="primary" />
      <div className="mt-2">
        <h3 className="mb-1">{STEP_CONFIG[currentStep]?.title}</h3>
        <small className="text-muted" style={{ fontSize: '1rem' }}>
          {STEP_CONFIG[currentStep]?.subtitle}
        </small>
      </div>
    </div>
  );
};

const StepSelectRecordType = ({
  recordTypes,
  isLoadingRecordTypes,
  onRecordTypeChange,
  selectedRecordType,
  isVisible
}) => {
  if (isLoadingRecordTypes) {
    return <div>Carregando tipos de registro...</div>;
  }

  return (
    <div style={{ display: isVisible ? 'block' : 'none' }}>
      <RecordTypeSelection
        recordTypes={recordTypes || []}
        handleOnSelectedRecordType={onRecordTypeChange}
        selectedRecordType={selectedRecordType}
        showConfirmationButton={false}
      />
    </div>
  );
};

const StepMapDefaultFields = ({
  fieldsObject,
  reasons,
  subreasons,
  statuses,
  isLoadingReasons,
  reasonId,
  selectedRecordType,
  control,
  errors,
  setValue,
  getValidationRules,
  isVisible
}) => {
  const renderReasonField = () => (
    <ConditionalFieldRenderer
      fieldsObject={fieldsObject}
      fieldType="reason_id"
      fallback={<div>Nenhum motivo disponível</div>}
    >
      <Row className="mb-3">
        <Col sm="12">
          <label className="form-control-label">Motivo*</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={reasons.map(({ id, name }) => ({
                  label: name,
                  value: id
                }))}
                value={props.value}
                onChange={props.onChange}
                valueController={setValue}
                fieldName="reason_id"
                labelName="label"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={true}
                isDisabled={!selectedRecordType || isLoadingReasons}
                placeholder={
                  isLoadingReasons
                    ? 'Carregando motivos...'
                    : 'Selecione o motivo'
                }
              />
            )}
            control={control}
            name="reason_id"
            defaultValue=""
            rules={getValidationRules('reason_id')}
          />
          <ErrorMessage
            errors={errors}
            name="reason_id"
            render={({ message }) => <p style={{ color: 'red' }}>{message}</p>}
          />
        </Col>
      </Row>
    </ConditionalFieldRenderer>
  );

  const renderSubreasonField = () => (
    <ConditionalFieldRenderer
      fieldsObject={fieldsObject}
      fieldType="subreason_id"
      fallback={<div>Nenhum submotivo disponível</div>}
    >
      <Row className="mb-3">
        <Col sm="12">
          <label className="form-control-label">Submotivo*</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={subreasons.map(({ id, name }) => ({
                  label: name,
                  value: id
                }))}
                value={props.value}
                onChange={props.onChange}
                valueController={setValue}
                fieldName="subreason_id"
                labelName="label"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={true}
                isDisabled={!reasonId || subreasons.length === 0}
                placeholder={
                  subreasons.length === 0
                    ? 'Selecione primeiramente um motivo'
                    : 'Selecione o submotivo'
                }
              />
            )}
            control={control}
            name="subreason_id"
            defaultValue=""
            rules={getValidationRules('subreason_id')}
          />
          <ErrorMessage
            errors={errors}
            name="subreason_id"
            render={({ message }) => <p style={{ color: 'red' }}>{message}</p>}
          />
        </Col>
      </Row>
    </ConditionalFieldRenderer>
  );

  const renderStatusField = () => (
    <ConditionalFieldRenderer
      fieldsObject={fieldsObject}
      fieldType="current_status"
      fallback={<div>Nenhum status disponível</div>}
    >
      <Row className="mb-3">
        <Col sm="12">
          <label className="form-control-label">Status*</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={statuses.map(({ id, name }) => ({
                  label: name,
                  value: id
                }))}
                value={props.value}
                onChange={props.onChange}
                valueController={setValue}
                fieldName="current_status"
                labelName="label"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={true}
                isDisabled={!selectedRecordType || statuses.length === 0}
                placeholder={
                  statuses.length === 0
                    ? 'Nenhum status disponível'
                    : 'Selecione o status'
                }
              />
            )}
            control={control}
            name="current_status"
            defaultValue=""
            rules={getValidationRules('current_status')}
          />
          <ErrorMessage
            errors={errors}
            name="current_status"
            render={({ message }) => <p style={{ color: 'red' }}>{message}</p>}
          />
        </Col>
      </Row>
    </ConditionalFieldRenderer>
  );

  return (
    <div style={{ display: isVisible ? 'block' : 'none' }}>
      {renderReasonField()}
      {renderSubreasonField()}
      {renderStatusField()}
    </div>
  );
};

const StepMapCustomFields = ({
  fields,
  control,
  errors,
  watch,
  setValue,
  Controller,
  isVisible,
  onOpenAutoMappingModal
}) => {
  return (
    <div style={{ display: isVisible ? 'block' : 'none' }}>
      <Row className="mb-4">
        <Col className="d-flex justify-content-center">
          <Button
            color="primary"
            type="button"
            size="lg"
            style={{ minWidth: '220px', fontWeight: 'bold' }}
            onClick={onOpenAutoMappingModal}
          >
            Mapear automaticamente
          </Button>
        </Col>
      </Row>

      <CustomFieldsInputsForm
        fields={fields}
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        Controller={Controller}
      />
    </div>
  );
};

const StepSLARecalculation = ({ recordType, isVisible }) => {
  const isSlaActive = !!recordType?.is_sla_active;

  return (
    <div style={{ display: isVisible ? 'block' : 'none' }}>
      {isSlaActive ? (
        <div>
          <p>
            O SLA está ativo para este tipo de registro. Ao alterar o tipo de
            registro, o SLA será alterado.
          </p>

          <AlertBox type="warning" style={{ width: '42.5rem' }}>
            <strong>Observação: </strong>
            Para que o SLA seja alterado, o campo &#39;Conta&#39; deve estar
            ativo e preenchido para o tipo de registro selecionado.
          </AlertBox>
        </div>
      ) : (
        <p>
          O SLA não está ativo para este protocolo. Nenhuma alteração de SLA
          será realizada.
        </p>
      )}
    </div>
  );
};

const StepSetOptionalDescription = ({ control, isVisible }) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
    }
  };

  return (
    <div style={{ display: isVisible ? 'block' : 'none' }}>
      <Row className="mb-3">
        <Col sm="12">
          <Label className="form-control-label">Descrição da mudança</Label>
          <Controller
            name="changeReason"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Input
                type="textarea"
                rows="3"
                placeholder="Descreva o motivo da mudança (opcional)"
                onKeyDown={handleKeyDown}
                {...field}
              />
            )}
          />
        </Col>
      </Row>
    </div>
  );
};

const ModalRecordType = ({
  isModalOpen,
  onModalToggle,
  onSelected,
  protocolId,
  prevRecordTypeId,
  isAutoMappingModalOpen,
  onAutoMappingModalToggle
}) => {
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_RECORD_TYPE);
  const [selectedRecordType, setSelectedRecordType] = useState(null);

  const { recordTypes, isLoadingRecordTypes, getRecordTypesByUser } =
    useRecordTypesByUser(entitiesName.PROTOCOL);

  const validRecordTypes = (recordTypes || []).filter(
    (elem) => elem.id !== prevRecordTypeId
  );

  const { fieldsObject, fetchFieldsObject } = useDefaultFieldsObject(
    prevRecordTypeId,
    selectedRecordType
  );

  const { reasons, isLoadingReasons } = useReasons(
    selectedRecordType,
    isModalOpen
  );

  const { statuses } = useStatuses(selectedRecordType, isModalOpen);

  const { customFields, fetchCustomFields } =
    useCustomFields(selectedRecordType);

  const {
    prevCustomFieldsValues,
    customFieldsValues,
    setCustomFieldsValues,
    fetchCustomFieldsValues
  } = useCustomFieldsValues(protocolId, prevRecordTypeId, selectedRecordType);

  const { recordType, fetchRecordType } =
    useSelectedRecordType(selectedRecordType);

  const availableCustomFields = customFieldsValues?.filter(
    (elem) => elem.type !== 'LIST' && elem.type !== 'MULTIPLE'
  );
  const availablePrevCustomFields = prevCustomFieldsValues?.filter(
    (elem) => elem.type !== 'LIST' && elem.type !== 'MULTIPLE'
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      reason_id: '',
      subreason_id: '',
      current_status: '',
      changeReason: '',
      autoMappingPrevFields: '',
      autoMappingFields: ''
    }
  });

  const reasonId = watch('reason_id');
  const subreasonId = watch('subreason_id');
  const statusId = watch('current_status');

  const selectedCustomFieldType = watch('autoMappingFields');
  const autoMappingPrevField = watch('autoMappingPrevFields');

  const { subreasons } = useSubreasons(reasonId);

  // Validação condicional
  const getValidationRules = (fieldType) => {
    const shouldRender = fieldsObject?.[fieldType]?.new?.isRequired;
    return shouldRender ? { required: 'Este campo é obrigatório' } : {};
  };

  const handleRecordTypeChange = useCallback(
    (id) => {
      setSelectedRecordType(id);
      setValue('selectedRecordType', id);
    },
    [setValue]
  );

  const goToNextStep = useCallback(() => {
    const steps = Object.keys(STEPS);
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  }, [currentStep]);

  const goToPreviousStep = useCallback(() => {
    const steps = Object.keys(STEPS);
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  }, [currentStep]);

  const canProceedToNext = useCallback(() => {
    switch (currentStep) {
      case STEPS.SELECT_RECORD_TYPE:
        return selectedRecordType !== null;
      case STEPS.MAP_DEFAULT_FIELDS: {
        if (fieldsObject.reason_id?.new?.isActive && !reasonId) return false;
        if (fieldsObject.subreason_id?.new?.isActive && !subreasonId)
          return false;
        if (fieldsObject.current_status?.new?.isActive && !statusId)
          return false;
        return true;
      }
      case STEPS.MAP_CUSTOM_FIELDS: {
        const customFieldsData = watch();
        return customFields.every((field) => {
          const isRequired = field.recordTypesConfig?.isRequired;
          if (isRequired) {
            return (
              customFieldsData[field.name] !== undefined &&
              customFieldsData[field.name] !== ''
            );
          }
          return true;
        });
      }
      case STEPS.CHANGE_SLA:
        return true;
      case STEPS.OPTIONAL_DESCRIPTION:
        return true;
      default:
        return true;
    }
  }, [
    currentStep,
    selectedRecordType,
    fieldsObject,
    reasonId,
    subreasonId,
    statusId,
    watch,
    customFields
  ]);

  const selectedFieldInfo = useMemo(() => {
    if (!selectedCustomFieldType) return { type: '', name: '' };

    const availableField = availableCustomFields.find(
      (field) => field.id === selectedCustomFieldType
    );
    if (!availableField) return { type: '', name: '' };

    return {
      type: availableField.type,
      name: availableField.name
    };
  }, [selectedCustomFieldType]);

  const availablePrevFieldsForMapping = useMemo(() => {
    if (!selectedFieldInfo || !availablePrevCustomFields) return [];

    return availablePrevCustomFields
      .filter((elem) => elem.type === selectedFieldInfo.type && elem.value)
      .map(({ label, id }) => ({ label, value: id }));
  }, [selectedFieldInfo, availablePrevCustomFields]);

  useEffect(() => {
    if (isModalOpen) {
      getRecordTypesByUser();
      setSelectedRecordType(null);
      setCurrentStep(STEPS.SELECT_RECORD_TYPE);
      reset({
        reason_id: '',
        subreason_id: '',
        current_status: '',
        changeReason: ''
      });
    }
  }, [isModalOpen, getRecordTypesByUser, reset]);

  useEffect(() => {
    if (!isLoadingRecordTypes && recordTypes && !selectedRecordType) {
      setSelectedRecordType(recordTypes[0]?.id || null);
    }
  }, [isLoadingRecordTypes, recordTypes, selectedRecordType]);

  useEffect(() => {
    const loadData = async () => {
      if (selectedRecordType) {
        await fetchFieldsObject();
        await fetchCustomFields();
        await fetchCustomFieldsValues();
        await fetchRecordType();
        reset((prev) => ({
          ...prev,
          reason_id: '',
          subreason_id: '',
          current_status: '',
          changeReason: prev?.changeReason || ''
        }));
      }
    };

    loadData();
  }, [
    selectedRecordType,
    fetchFieldsObject,
    fetchCustomFields,
    fetchCustomFieldsValues,
    setCustomFieldsValues,
    reset
  ]);

  useEffect(() => {
    if (reasonId) {
      setValue('subreason_id', '');
    }
  }, [reasonId, setValue]);

  const parseValuesByType = (value, type) => {
    if (type === 'DATE') {
      return value ? new Date(value) : '';
    }
    if (type === 'BOOLEAN') {
      return value === 'true' || value === true;
    }
    if (type === 'NUMBER') {
      return Number(value);
    }
    return value;
  };

  const parseValuesByTypeHandlerDTO = (value, type) => {
    if (type === 'DATE') {
      return value ? new Date(value) : '';
    }
    if (type === 'BOOLEAN') {
      return value === 'true' || value === true;
    }
    if (type === 'NUMBER') {
      return Number(value);
    }

    if (type === 'CPF') {
      return value.replace(/\D/g, '');
    }

    if (type === 'CNPJ') {
      return value.replace(/\D/g, '');
    }

    return value;
  };

  const refreshPage = () => {
    const values = {
      recordTypeId: selectedRecordType,
      returnPath: '/admin/protocol'
    };
    localStorage.setItem('valueRoute', JSON.stringify(values));
    window.location.reload();
  };

  const handleFinishClick = () => {
    handleSubmit(onSubmit)();
  };

  const handleDataDTO = ({
    protocolId,
    prevRecordTypeId,
    selectedRecordType,
    customFields,
    data
  }) => {
    const login = localStorage.getItem('login');

    const fields = customFields.reduce(
      (acc, field) => {
        if (!field?.recordTypesConfig) {
          throw new Error(
            `Campo customizado ${field.name} não possui configuração.`
          );
        }

        const isFieldRequired = field.recordTypesConfig.isRequired;

        if (isFieldRequired) {
          if (!data[field.name]) {
            throw new Error(
              `Campo obrigatório ${field.name} não foi preenchido.`
            );
          }

          acc.required[field.name] = parseValuesByTypeHandlerDTO(
            data[field.name],
            field.type
          );
        } else if (data[field.name]) {
          acc.optional[field.name] = parseValuesByTypeHandlerDTO(
            data[field.name],
            field.type
          );
        }

        return acc;
      },
      {
        required: {},
        optional: {}
      }
    );

    return {
      protocol_id: protocolId,
      prevRecordTypeId,
      newRecordTypeId: Number(selectedRecordType),
      login,
      fields,
      reason_id: data.reason_id,
      subreason_id: data.subreason_id,
      current_status: data.current_status,
      changeReason: data.changeReason
    };
  };

  const onSubmit = useCallback(
    async (data) => {
      if (!selectedRecordType) return;

      const dataDTO = handleDataDTO({
        protocolId,
        prevRecordTypeId,
        selectedRecordType,
        customFields,
        data
      });

      try {
        await ProtocolService.changeRecordType(dataDTO);
        toast.success('Tipo de registro alterado com sucesso!', {
          autoClose: 3000
        });
        setTimeout(() => {
          onModalToggle(false);
          if (onSelected) onSelected();
          refreshPage();
        }, 2000);
      } catch (error) {
        console.error('Erro ao alterar tipo de registro:', error);
        toast.error(
          `Erro ao alterar tipo de registro: ${
            error?.message || ''
          }. Tente novamente.`,
          {
            autoClose: 5000
          }
        );
      }
    },
    [
      currentStep,
      handleDataDTO,
      selectedRecordType,
      protocolId,
      prevRecordTypeId,
      customFields,
      onSelected,
      onModalToggle
    ]
  );

  return (
    <>
      <DefaultModal
        show={isModalOpen}
        onModalToggle={onModalToggle}
        title="Alterar Tipo de Registro"
        subtitle="Siga as etapas para configurar a mudança do tipo de registro"
        modalContainerClasses="modal-xl"
        showCloseButton={false}
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <StepIndicator currentStep={currentStep} />
          <hr />
          <div
            style={{
              minHeight: '19.5rem',
              maxHeight: '25rem',
              overflowY: 'scroll',
              overflowX: 'hidden'
            }}
          >
            <StepSelectRecordType
              recordTypes={validRecordTypes}
              isLoadingRecordTypes={isLoadingRecordTypes}
              onRecordTypeChange={handleRecordTypeChange}
              selectedRecordType={selectedRecordType}
              isVisible={currentStep === STEPS.SELECT_RECORD_TYPE}
            />

            <StepMapDefaultFields
              fieldsObject={fieldsObject}
              reasons={reasons}
              subreasons={subreasons}
              statuses={statuses}
              isLoadingReasons={isLoadingReasons}
              reasonId={reasonId}
              selectedRecordType={selectedRecordType}
              control={control}
              errors={errors}
              setValue={setValue}
              getValidationRules={getValidationRules}
              isVisible={currentStep === STEPS.MAP_DEFAULT_FIELDS}
            />

            <StepMapCustomFields
              fields={customFields}
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
              Controller={Controller}
              isVisible={currentStep === STEPS.MAP_CUSTOM_FIELDS}
              onOpenAutoMappingModal={() => onAutoMappingModalToggle(true)}
            />

            <AutoMappingModal
              show={isAutoMappingModalOpen}
              onToggle={onAutoMappingModalToggle}
              control={control}
              setValue={setValue}
              availableCustomFields={availableCustomFields}
              availablePrevCustomFields={availablePrevCustomFields}
              selectedFieldInfo={selectedFieldInfo}
              autoMappingPrevField={autoMappingPrevField}
              availablePrevFieldsForMapping={availablePrevFieldsForMapping}
              parseValuesByType={parseValuesByType}
            />

            <StepSLARecalculation
              recordType={recordType}
              isVisible={currentStep === STEPS.CHANGE_SLA}
            />

            <StepSetOptionalDescription
              control={control}
              isVisible={currentStep === STEPS.OPTIONAL_DESCRIPTION}
            />
          </div>

          <hr />

          <div className="d-flex justify-content-between">
            <div>
              {currentStep !== STEPS.SELECT_RECORD_TYPE && (
                <Button
                  color="secondary"
                  type="button"
                  onClick={goToPreviousStep}
                >
                  Anterior
                </Button>
              )}
            </div>

            <div className="d-flex gap-2">
              <Button
                color={
                  currentStep === STEPS.OPTIONAL_DESCRIPTION
                    ? 'success'
                    : 'primary'
                }
                type="button"
                onClick={
                  currentStep === STEPS.OPTIONAL_DESCRIPTION
                    ? handleFinishClick
                    : goToNextStep
                }
                disabled={!canProceedToNext()}
              >
                {currentStep === STEPS.OPTIONAL_DESCRIPTION
                  ? 'Finalizar'
                  : 'Próximo'}
              </Button>

              <Button
                color="danger"
                type="button"
                onClick={() => onModalToggle(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </Form>
      </DefaultModal>
    </>
  );
};

export default ModalRecordType;
